import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DashboardHeader } from "../components/DashboardHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, Building2, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AccountPage() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState({
    clinic_name: user?.clinic_name || "", contact_name: user?.contact_name || "", phone: user?.phone || "",
    fiscal_name: user?.fiscal_name || "", cif: user?.cif || "", fiscal_address: user?.fiscal_address || "",
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/auth/profile", profile);
      setUser(data);
      toast.success("Datos actualizados");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingProfile(false); }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) { toast.error("Las contraseñas nuevas no coinciden"); return; }
    if (pwd.new_password.length < 6) { toast.error("La nueva contraseña debe tener al menos 6 caracteres"); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Contraseña actualizada");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingPwd(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <DashboardHeader subtitle="Mi cuenta" />
      <main className="max-w-3xl mx-auto px-6 py-8">
        <Link to="/agenda" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#64748B] hover:text-[#373E45] mb-4" data-testid="back-to-agenda">
          <ArrowLeft className="h-4 w-4" />Volver a la agenda
        </Link>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#373E45] mb-6">Gestión de la cuenta</h1>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 mb-6">
          <h2 className="font-bold text-[#373E45] flex items-center gap-2 mb-1"><Building2 className="h-5 w-5 text-[#7FD498]" />Datos de la clínica</h2>
          <p className="text-sm text-[#64748B] mb-6">Mantén tus datos de contacto actualizados.</p>
          <form onSubmit={saveProfile} className="space-y-5">
            <div>
              <Label className="text-[#373E45] font-semibold">Email</Label>
              <div className="mt-1.5 flex items-center gap-2 bg-slate-100 rounded-md px-3 h-10 text-[#64748B] text-sm" data-testid="account-email">
                <Mail className="h-4 w-4" />{user?.email}
              </div>
              <p className="text-xs text-slate-400 mt-1">El email no se puede modificar.</p>
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Nombre de la clínica</Label>
              <Input value={profile.clinic_name} onChange={(e) => setProfile({ ...profile, clinic_name: e.target.value })} required className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="account-clinic-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#373E45] font-semibold">Persona de contacto</Label>
                <Input value={profile.contact_name} onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })} required className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="account-contact-input" />
              </div>
              <div>
                <Label className="text-[#373E45] font-semibold">Teléfono</Label>
                <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="account-phone-input" />
              </div>
            </div>
            <div className="pt-4 border-t border-slate-100">
              <p className="text-xs font-bold text-[#64748B] uppercase mb-3">Datos de facturación</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-[#373E45] font-semibold">Nombre fiscal</Label>
                  <Input value={profile.fiscal_name} onChange={(e) => setProfile({ ...profile, fiscal_name: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Razón social" data-testid="account-fiscalname-input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#373E45] font-semibold">CIF</Label>
                    <Input value={profile.cif} onChange={(e) => setProfile({ ...profile, cif: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="B12345678" data-testid="account-cif-input" />
                  </div>
                  <div>
                    <Label className="text-[#373E45] font-semibold">Dirección fiscal</Label>
                    <Input value={profile.fiscal_address} onChange={(e) => setProfile({ ...profile, fiscal_address: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Calle, nº, CP, ciudad" data-testid="account-fiscaladdress-input" />
                  </div>
                </div>
              </div>
            </div>
            <Button type="submit" disabled={savingProfile} className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid="save-profile-btn">
              {savingProfile ? <Loader2 className="h-5 w-5 animate-spin" /> : "Guardar cambios"}
            </Button>
          </form>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="font-bold text-[#373E45] flex items-center gap-2 mb-1"><KeyRound className="h-5 w-5 text-[#7FD498]" />Cambiar contraseña</h2>
          <p className="text-sm text-[#64748B] mb-6">Introduce tu contraseña actual y la nueva.</p>
          <form onSubmit={savePwd} className="space-y-5 max-w-md">
            <div>
              <Label className="text-[#373E45] font-semibold">Contraseña actual</Label>
              <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} required className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Nueva contraseña</Label>
              <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} required minLength={6} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Repite la nueva contraseña</Label>
              <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={savingPwd} className="bg-[#373E45] hover:bg-[#2b3137] text-white font-bold rounded-xl" data-testid="save-password-btn">
              {savingPwd ? <Loader2 className="h-5 w-5 animate-spin" /> : "Actualizar contraseña"}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
